import { createElement, forwardRef } from 'react';

import { toCss } from './reactNative.jsx';

// Web stand-in for react-native-svg, for app source rendered in place on the web
// (the Watch App help illustrations, the app's icon set). react-native-svg's
// component names are the SVG element names in PascalCase and its props are the
// SVG attributes in React DOM's own camelCase, so every component here is the
// matching DOM element with the props passed straight through — with two
// translations. `style`: react-native-svg takes a React Native style (an array, a
// transform list, bare numbers) where the DOM wants CSS, so the react-native shim
// converts it. Attribute names: react-native-svg accepts the SVG spellings
// (stroke-linejoin) as well as React's (strokeLinejoin) and the app's drawings use
// both, where React DOM warns on every render about the former; they are
// camel-cased here (data-* and aria-* excepted). Nothing is redrawn: a change to
// a shared drawing reaches the web at the next build.
//
// Wired in by plugins/appShims.js, which answers a bare `react-native-svg` import
// from any file under app/src with this module (the web's own code never imports
// it). The whole common element set is exported, not just what today's shared
// files use, so a new element in a shared drawing does not arrive as `undefined`
// and fail at render time.
const camelCase = (name) =>
  name.startsWith('data-') || name.startsWith('aria-')
    ? name
    : name.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());

function svgElement(tag) {
  const Element = forwardRef(function SvgElement({ style, ...props }, ref) {
    const domProps = {};
    for (const [name, value] of Object.entries(props)) {
      domProps[camelCase(name)] = value;
    }
    if (style !== undefined) {
      domProps.style = toCss(style);
    }
    domProps.ref = ref;
    return createElement(tag, domProps);
  });
  Element.displayName = `Svg.${tag}`;
  return Element;
}

export const Svg = svgElement('svg');
export default Svg;

export const Circle = svgElement('circle');
export const ClipPath = svgElement('clipPath');
export const Defs = svgElement('defs');
export const Ellipse = svgElement('ellipse');
export const ForeignObject = svgElement('foreignObject');
export const G = svgElement('g');
export const Line = svgElement('line');
export const LinearGradient = svgElement('linearGradient');
export const Marker = svgElement('marker');
export const Mask = svgElement('mask');
export const Path = svgElement('path');
export const Pattern = svgElement('pattern');
export const Polygon = svgElement('polygon');
export const Polyline = svgElement('polyline');
export const RadialGradient = svgElement('radialGradient');
export const Rect = svgElement('rect');
export const Stop = svgElement('stop');
export const TextPath = svgElement('textPath');
export const TSpan = svgElement('tspan');
export const Use = svgElement('use');

// These three share a name with a browser global (Image, Symbol, the DOM Text
// node), so they are built under another name and exported under the SVG one.
const ImageElement = svgElement('image');
const SymbolElement = svgElement('symbol');
const TextElement = svgElement('text');
export { ImageElement as Image, SymbolElement as Symbol, TextElement as Text };
